import { formatDate } from "date-fns";
import { download, generateCsv } from "export-to-csv";

import { IPSearchAnalyticsService } from "@analytics/index";

import { CompanyType } from "@interfaces/company.type";

import { ICompanyTableControls } from "@store/filters/filter.types";

import { csvExportConfig, getValueOrDefault } from "@utilities/tableExport";

/**
 * Filters a company based on the estimated number of employees.
 *
 * @param company - The company object to filter.
 * @param minEmployees - The minimum number of employees a company should have.
 * @param maxEmployees - The maximum number of employees a company should have.
 * @returns Returns true if the company's estimated number of employees is within the provided range, otherwise returns false.
 */
const sizeFilter = (company: CompanyType, minEmployees: number, maxEmployees: number) =>
  company.estimatedNumEmployees >= minEmployees && company.estimatedNumEmployees <= maxEmployees;

/**
 * Filters a company based on the provided keywords.
 *
 * @param company - The company object to filter.
 * @param keywords - An array of keywords to match against the company data.
 * @returns Returns true if all keywords are found in the company data, otherwise returns false.
 */
const keywordFilter = (company: CompanyType, keywords: string[] | undefined) => {
  if (!keywords || keywords.length === 0) return true;

  const targetString = JSON.stringify(company).toLowerCase();

  return keywords.some((keyword) => targetString.includes(keyword.toLowerCase()));
};

/**
 * Filters a company based on the provided locations.
 *
 * @param company - The company object to filter.
 * @param locations - An array of locations to match against the company data.
 * @returns Returns true if all locations are found in the company data, otherwise returns false.
 */
const locationFilter = (company: CompanyType, locations: string[] | undefined) => {
  if (!locations || locations.length === 0) return true;

  const locationFields = ["rawAddress", "streetAddress", "city", "state", "country", "postalCode"];

  const locationString = locationFields
    .map((field) => {
      const value = company[field as keyof CompanyType];
      // Explicitly check type to ensure it's a string
      return typeof value === "string" ? value : "";
    })
    .join(" ")
    .toLowerCase();

  return locations.some((location) => locationString.includes(location.toLowerCase()));
};

/**
 * Filters a company based on the provided locations.
 *
 * @param company - The company object to filter.
 * @param industries - An array of industries to match against the company data.
 * @returns Returns true if all industries are found in the company data, otherwise returns false.
 */
const industryFilter = (company: CompanyType, industries: string[] | undefined) => {
  if (!industries || industries.length === 0) return true;
  const industryString = company.industries.join(" ").toLowerCase();
  return industries.some((industry) => industryString.includes(industry.toLowerCase()));
};

/**
 * Checks if a given company property has a frequency \>1. Returning true means, we do not filter this company.
 *
 * @param companyProperty - The property which the filter is checking against.
 * @param filter - The filter to be applied. If false (The filter is turned off), the filter will be ignored.
 * @returns Returns true if the filter is false (off) or the corresponding company property is greater than 0, otherwise returns false.
 */
const booleanFilter = (companyProperty: boolean, filter: boolean) => !filter || companyProperty;

const filterFunctions = {
  autm2023: (company: CompanyType, filters: ICompanyTableControls["filters"]) =>
    booleanFilter(company.hasAutm2023, !!filters.autm2023),
  autm2024: (company: CompanyType, filters: ICompanyTableControls["filters"]) =>
    booleanFilter(company.hasAutm2024, !!filters.autm2024),
  bio2022: (company: CompanyType, filters: ICompanyTableControls["filters"]) =>
    booleanFilter(company.hasBio2022, !!filters.bio2022),
  bio2023: (company: CompanyType, filters: ICompanyTableControls["filters"]) =>
    booleanFilter(company.hasBio2023, !!filters.bio2023),
  bio2024: (company: CompanyType, filters: ICompanyTableControls["filters"]) =>
    booleanFilter(company.hasBio2024, !!filters.bio2024),
  forProfit: (company: CompanyType, filters: ICompanyTableControls["filters"]) => {
    if (filters.nonProfit && filters.forProfit) {
      return true;
    }
    return !filters.forProfit || company.forProfit;
  },
  govLicense: (company: CompanyType, filters: ICompanyTableControls["filters"]) =>
    booleanFilter(company.hasGovinfo, !!filters.govLicense),
  industries: (company: CompanyType, filters: ICompanyTableControls["filters"]) =>
    industryFilter(company, filters.industries),
  keywords: (company: CompanyType, filters: ICompanyTableControls["filters"]) =>
    keywordFilter(company, filters.keywords),
  les2023: (company: CompanyType, filters: ICompanyTableControls["filters"]) =>
    booleanFilter(company.hasLes2023, !!filters.les2023),
  licensed: (company: CompanyType, filters: ICompanyTableControls["filters"]) =>
    booleanFilter(company.hasSec, !!filters.licensed),
  locations: (company: CompanyType, filters: ICompanyTableControls["filters"]) =>
    locationFilter(company, filters.locations),
  nonProfit: (company: CompanyType, filters: ICompanyTableControls["filters"]) => {
    if (filters.nonProfit && filters.forProfit) {
      return true;
    }
    return !filters.nonProfit || !company.forProfit;
  },
  sbir: (company: CompanyType, filters: ICompanyTableControls["filters"]) =>
    booleanFilter(company.hasSbir, !!filters.sbir),
  sponsored: (company: CompanyType, filters: ICompanyTableControls["filters"]) =>
    booleanFilter(company.hasSponsor, !!filters.sponsored),
  uidp2023: (company: CompanyType, filters: ICompanyTableControls["filters"]) =>
    booleanFilter(company.hasUidpMim2023, !!filters.uidp2023),
};

const filterCompanies = (companies: CompanyType[], filtersObject: ICompanyTableControls) => {
  const { filters } = filtersObject;
  return companies.filter((company) => {
    for (const filterKey in filters) {
      // Special case for sizeFilter which requires both minEmployees and maxEmployees
      if (
        (filterKey === "minEmployees" || filterKey === "maxEmployees") &&
        !sizeFilter(company, filters.minEmployees || 0, filters.maxEmployees || Infinity)
      ) {
        return false;
      }
      // Check if a corresponding filter function exists
      const filterFunction = filterFunctions[filterKey as keyof typeof filterFunctions];
      // If the filter function returns false, exclude the company
      if (filterFunction && !filterFunction(company, filters)) {
        return false;
      }
    }
    // If all filter functions return true, include the company
    return true;
  });
};

/**
 * Export companies based on a selected ids to a CSV file.
 *
 * @param selectedCompanyIds - An array of selected company IDs.
 * @param companies - An array of company objects.
 */
const exportCompanies = (selectedCompanyIds: string[], companies: CompanyType[]) => {
  // Check if any of the companies have a 'patents' field
  // const companiesHavePatents = companies.some((company) => company.hasPatents);

  const date = formatDate(new Date(), "MM-dd-yyyy");
  const customerFileName = `company_list_${date}`;

  const exportList = selectedCompanyIds.map((companyId) => {
    const company = companies.find((c) => c.id === companyId)!;

    /* eslint-disable perfectionist/sort-objects */ // Required for CSV export to have columns in correct order
    return {
      company: getValueOrDefault(company.name),
      website: getValueOrDefault(company.primaryDomain),
      city: getValueOrDefault(company.city),
      state: getValueOrDefault(company.state),
      country: getValueOrDefault(company.country),
      linkedin: getValueOrDefault(company.linkedinUrl),
      crunchbase_url: getValueOrDefault(company.crunchbaseUrl),
      estimated_num_employees: getValueOrDefault(company.estimatedNumEmployees),
      founded_year: getValueOrDefault(company.foundedYear),
      industries: company.industries ? company.industries.join(", ") : "nan",
      // TODO: Conditionally add the 'patents' field if companies have patents -- !?
      // THIS NEVER WORKED BECAUSE COMPANIES ARE NOT QUERIED WITH PATENTS IN THIS DATA SET. wE NEED TO EITHER GET COMPANIES
      // WITH PATENTS IN INITIAL QUERY OR QUERY FOR PATENTS HERE
      // ...(companiesHavePatents
      //   ? { patents: company.patents ? company.patents.map((patent) => `${patent.id}`).join(", ") : "nan" }
      //   : {}),
      description: getValueOrDefault(company.shortDescription),
    };
    /* eslint-enable perfectionist/sort-objects */
  });

  const csv = generateCsv(csvExportConfig(customerFileName))(exportList);
  download(csvExportConfig(customerFileName))(csv);

  IPSearchAnalyticsService.trackCompaniesDownloaded(selectedCompanyIds.length);
};

export default {
  exportCompanies,
  filterCompanies,
};
